// The one act a toolkit's scheme derives for this person (02-DESIGN/08-toolkits/
// 03-linking.md § discovering what needs linking): *Link*, which runs the
// consent flow and deposits on the person's own connection; *Unlink*, with
// the plain sentence about revocation; *Connect*, the form the setup schema
// describes for a pasted key, stored at the same path, a form in the page's
// or the tab's place (01-the-console.md § conventions: a form); *Remove* for
// one. A shared or public toolkit offers nothing: it is configured by
// definition.

import { useState } from "react";

import { usePage } from "../../platform/session.tsx";
import { Button, Confirm, Field, NotAllowed, TextInput } from "../../ui/components.tsx";
import { FormPage } from "../../ui/form.tsx";
import type { MenuEntry } from "../../ui/table.tsx";
import { linkPath, type Toolkit } from "../../wire/toolkit.ts";
import type { Workload } from "../../wire/workloads.ts";
import { useSurface } from "../surface.tsx";
import { plainState } from "../workloads/state.ts";
import { beginLink } from "./link.ts";
import {
  actOf,
  DISCONNECT_CONSEQUENCE,
  setupFields,
  UNLINK_CONSEQUENCE,
  type SetupField,
  type Standing,
} from "./state.ts";

/**
 * whyNot says why linking is not possible right now, or nothing. Where the
 * vault does not answer, it names the vault workload's status as the Secrets
 * and Stores pages do: the vault is placed at the workspace's creation
 * (decision 0021), so it is starting, stopped or failing, or not among the
 * workspace's workloads yet; `vault` is undefined where the person cannot
 * read the workloads.
 */
export function whyNot(
  principal: string,
  noVault: boolean,
  workspace: string,
  vault?: Pick<Workload, "state" | "placement"> | null,
): string | undefined {
  if (noVault) {
    if (vault === undefined) {
      return `${workspace}'s vault is not answering yet, so nothing can be stored for you. Linking is offered the moment it answers.`;
    }
    if (vault === null) {
      return `${workspace}'s vault is placed when the workspace is created, and it is not among ${workspace}'s workloads yet. Linking is offered the moment it answers.`;
    }
    return `${workspace}'s vault is not answering yet: it runs as a workload of ${workspace}'s own, now ${plainState(vault).word}. Linking is offered the moment it answers.`;
  }
  if (principal === "") {
    return "Your sign-in does not carry what linking needs yet. Sign in again once the workspace's sign-in has been updated.";
  }
  return undefined;
}

export function ToolkitActs({
  tk,
  standing,
  noVault,
  onChanged,
  onError,
  onConnect,
}: {
  tk: Toolkit;
  standing: Standing;
  noVault: boolean;
  onChanged: () => void;
  onError: (err: unknown) => void;
  /** onConnect opens *Connect* in the tab's place. */
  onConnect: () => void;
}) {
  const s = useSurface();
  const { origin } = usePage();
  const [busy, setBusy] = useState(false);
  const act = actOf(standing);
  const blocked = whyNot(s.principal, noVault, s.name);
  if (act === undefined) {
    return null;
  }
  const remove = (consequence: string, label: string) => (
    <Confirm
      label={label}
      consequence={consequence}
      disabled={busy || blocked !== undefined}
      onConfirm={() => {
        setBusy(true);
        s.bus
          .vault()
          .delete(linkPath(s.principal, tk), true)
          .then(
            () => {
              setBusy(false);
              onChanged();
            },
            (e: unknown) => {
              setBusy(false);
              onError(e);
            },
          );
      }}
    />
  );
  switch (act) {
    case "link":
      return (
        <Button
          kind="primary"
          size="sm"
          disabled={busy || blocked !== undefined}
          onClick={() => {
            setBusy(true);
            beginLink(origin, s.tenant, tk).then(
              (url) => {
                window.location.assign(url);
              },
              (e: unknown) => {
                setBusy(false);
                onError(e);
              },
            );
          }}
        >
          {busy ? "Opening the provider…" : "Link"}
        </Button>
      );
    case "unlink":
      return remove(UNLINK_CONSEQUENCE, "Unlink");
    case "connect":
      return (
        <Button kind="primary" size="sm" disabled={blocked !== undefined} onClick={onConnect}>
          Connect
        </Button>
      );
    case "disconnect":
      return remove(DISCONNECT_CONSEQUENCE, "Remove");
  }
}

/**
 * useToolkitMenu is the same one act as a row menu on the Toolkits table
 * (01-the-console.md § conventions: a row's actions): *Link* or *Connect*,
 * or *Unlink* or *Remove* after the divider, asked first. Nothing is offered
 * while linking is not possible; the page says why. *Connect* calls
 * onConnect, and the page shows the form in its place.
 */
export function useToolkitMenu({
  noVault,
  onChanged,
  onError,
  onConnect,
}: {
  noVault: boolean;
  onChanged: () => void;
  onError: (err: unknown) => void;
  onConnect: (tk: Toolkit) => void;
}): { menu: (tk: Toolkit, standing: Standing) => MenuEntry[] } {
  const s = useSurface();
  const { origin } = usePage();
  const blocked = whyNot(s.principal, noVault, s.name);
  const remove = (tk: Toolkit) => {
    s.bus.vault().delete(linkPath(s.principal, tk), true).then(onChanged, onError);
  };
  const menu = (tk: Toolkit, standing: Standing): MenuEntry[] => {
    const act = actOf(standing);
    if (act === undefined || blocked !== undefined) {
      return [];
    }
    switch (act) {
      case "link":
        return [
          {
            label: "Link",
            onSelect: () => {
              beginLink(origin, s.tenant, tk).then((url) => {
                window.location.assign(url);
              }, onError);
            },
          },
        ];
      case "connect":
        return [
          {
            label: "Connect",
            onSelect: () => {
              onConnect(tk);
            },
          },
        ];
      case "unlink":
        return [
          {
            label: "Unlink",
            danger: true,
            confirm: { title: `Unlink ${tk.name}?`, consequence: UNLINK_CONSEQUENCE },
            onSelect: () => {
              remove(tk);
            },
          },
        ];
      case "disconnect":
        return [
          {
            label: "Remove",
            danger: true,
            confirm: { title: `Remove your key for ${tk.name}?`, consequence: DISCONNECT_CONSEQUENCE },
            onSelect: () => {
              remove(tk);
            },
          },
        ];
    }
  };
  return { menu };
}

/** connectForm is the name a page's form state gives *Connect* for one toolkit: one form per toolkit, by its name. */
export function connectForm(toolkit: string): string {
  return `connect:${toolkit}`;
}

/** connectingOf reads which toolkit a page's open form connects, if the form is a *Connect*. */
export function connectingOf(form: string | undefined): string | undefined {
  return form?.startsWith("connect:") === true ? form.slice("connect:".length) : undefined;
}

/**
 * ConnectRecord is the form a token toolkit's setup schema describes: the
 * record the person pastes, stored at their own path as JSON, typed once and
 * never shown again. A toolkit whose schema the console cannot read gets no
 * guessed form. It is drawn in the page's place from the Toolkits list, and
 * in the tab's place, heading "h2", on the toolkit's page.
 */
export function ConnectRecord({
  tk,
  back,
  heading,
  onClose,
  onConnected,
}: {
  tk: Toolkit;
  /** What the form stands in front of, drawn as its crumb. */
  back: string;
  heading?: "h1" | "h2";
  onClose: () => void;
  onConnected: () => void;
}) {
  const s = useSurface();
  const fields = setupFields(tk.setup?.schema);
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const complete = (fs: SetupField[]) => fs.every((f) => !f.required || (values[f.name] ?? "").trim() !== "");
  const ready = fields !== undefined && complete(fields);
  const submit = () => {
    if (busy || fields === undefined || !complete(fields)) {
      return;
    }
    const record: Record<string, string> = {};
    for (const f of fields) {
      const v = (values[f.name] ?? "").trim();
      if (v !== "") {
        record[f.name] = v;
      }
    }
    setBusy(true);
    setErr(undefined);
    s.bus
      .vault()
      .store(linkPath(s.principal, tk), JSON.stringify(record))
      .then(
        () => {
          setBusy(false);
          onConnected();
        },
        (e: unknown) => {
          setBusy(false);
          setErr(e);
        },
      );
  };
  return (
    <FormPage
      title={`Connect ${tk.name}`}
      line={`What you paste is stored for you alone and spent by ${tk.name} as you. It is never shown again, not even to you.`}
      back={back}
      heading={heading}
      onClose={onClose}
      foot={
        <>
          <Button kind="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button kind="primary" onClick={submit} disabled={busy || !ready}>
            {busy ? "Connecting…" : "Connect"}
          </Button>
        </>
      }
    >
      {tk.setup !== undefined && tk.setup.instructions !== "" && (
        <p className="small muted">{tk.setup.instructions}</p>
      )}
      {err !== undefined && <NotAllowed err={err} what="Connect" />}
      {fields === undefined ? (
        <p className="small bad">
          {tk.name} does not say what to paste, so nothing can be guessed here. Ask whoever added it.
        </p>
      ) : (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          {fields.map((f: SetupField) => (
            <Field key={f.name} label={f.title} hint={f.required ? undefined : "Optional."}>
              <TextInput
                value={values[f.name] ?? ""}
                onChange={(v) => {
                  setValues({ ...values, [f.name]: v });
                }}
                type="password"
                required={f.required}
                autoFocus={f === fields[0]}
              />
            </Field>
          ))}
        </form>
      )}
    </FormPage>
  );
}
